import { requestResult } from './oidcClients';

// admin#181 — partner inquiries: the contacts /partners saves with
// type "Partner" (somo-circle1-api src/modules/shopping/contact).
//
// These routes use isAdmin, not requireNonMmlAdmin like the OIDC client
// routes, so the screens' useNonMmlAdminAccess() gate is UX only.

const PARTNER_TYPE = 'Partner';

export const isPartnerInquiry = (contact) => contact?.type === PARTNER_TYPE;

const contactUrl = (path = '') => `${process.env.REACT_APP_API_BASE_URL}/shopping/contact${path}`;

// The contact's existing status values, under the names the partner process
// uses. No partner-specific enum (admin#181).
export const PARTNER_STATUSES = [
    { value: 'pending', label: 'New' },
    { value: 'in-progress', label: 'In review / onboarding' },
    { value: 'resolved', label: 'Live' },
    { value: 'closed', label: 'Declined' },
];

export const statusLabel = (value) => PARTNER_STATUSES.find((s) => s.value === value)?.label || value || '—';

// The API's maximum page size for the contact list.
export const MAX_LIMIT = 100;

// An API without api#766 ignores `type` and returns every contact, so drop
// anything that isn't a Partner contact rather than show it as an inquiry.
export async function listPartnerInquiries({ page = 1, limit = 10, status } = {}) {
    const qs = new URLSearchParams({ type: PARTNER_TYPE, page: String(page), limit: String(limit) });
    if (status) qs.set('status', status);
    const result = await requestResult(contactUrl(`/list?${qs}`));
    return { ...result, contacts: (result?.contacts || []).filter(isPartnerInquiry) };
}

export const getPartnerInquiry = (id) => requestResult(contactUrl(`/${encodeURIComponent(id)}`));

export const updatePartnerInquiry = (id, changes) => requestResult(contactUrl(`/${encodeURIComponent(id)}`), {
    method: 'PUT',
    body: JSON.stringify(changes),
});

// Candidates for the assignee picker. /users/list returns superadmins only to
// a superadmin who asks for role=superadmin, so this lists admins.
export async function listAdminUsers() {
    const qs = new URLSearchParams({ role: 'admin', userStatus: 'active', limit: String(MAX_LIMIT) });
    const result = await requestResult(`${process.env.REACT_APP_API_BASE_URL}/users/list?${qs}`);
    return result?.users || [];
}

// The contact has no organization field: /partners sends it as the subject
// ("Partner inquiry: Acme") and as the message's first line
// ("Organization: Acme"). Both are free text from an anonymous form, so the
// group starts at a non-space (with \s*(.+), a failing match was quadratic in
// the run of spaces) and only the start of each is read.
const SUBJECT_ORG = /^Partner inquiry:[ \t]*(\S[^\r\n]*)$/i;
const MESSAGE_ORG = /^Organization:[ \t]*(\S[^\r\n]*)$/im;
const ORG_SCAN_LENGTH = 300;

const head = (text) => String(text || '').slice(0, ORG_SCAN_LENGTH);

export function organizationOf(contact) {
    const match = SUBJECT_ORG.exec(head(contact?.subject)) || MESSAGE_ORG.exec(head(contact?.message));
    return match ? match[1].trim() : '';
}

// The public form's email check lets "a@b.io?cc=…&body=…" through, which a
// mailto: link would turn into a prefilled draft; link only plain addresses.
const PLAIN_EMAIL = /^[^\s@?&#]+@[^\s@?&#]+$/;
export const isPlainEmail = (email) => PLAIN_EMAIL.test(email || '');

export const fullName = (contact) => [contact?.name, contact?.lastName].filter(Boolean).join(' ');

// assignedTo comes back populated ({ _id, name, email }) from every contact
// route, or unset.
export const ownerName = (assignedTo) => (assignedTo ? assignedTo.name || assignedTo.email || 'Unknown' : '');

export const idOf = (ref) => (ref && typeof ref === 'object' ? ref._id : ref) || '';

// One line per inquiry in the Partner Clients picker.
export const inquiryOptionLabel = (contact) => {
    const org = organizationOf(contact);
    const who = [fullName(contact), contact.email].filter(Boolean).join(', ');
    return org ? `${org} (${who})` : who;
};
